import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Form } from "../../../../components/ui/form";
import { Button } from "../../../../components/ui/button";
import { SheetTitle } from "../../../../components/ui/sheet";
import { useToast } from "../../../../hooks/use-toast";
import { FormInput } from "../../../../components/form-components/form-input.components";
import { useEffect } from "react";
import Spinner from "../../../../components/spinner.components";
import { useVerifyPanAdminHttp } from "../../../../hooks/http/admin/vendors/verify-pan.admin.http";
import { handleHttpError } from "../../../../utils/utils";
import { useParams } from "react-router-dom";
import useVendorStore from "../../../../store/vendor.store";

const FormSchema = z.object({
  pan: z
    .string()
    .min(10, { message: "Enter valid PAN number" })
    .max(10, { message: "Enter valid PAN number" }),
  fullName: z.string().min(1, { message: "Enter full name" }),
});

export default function AddPANAdminPartial() {
  const { execute: verifyPanHttp, fetching: verfiyingPan } =
    useVerifyPanAdminHttp();

  const { toast } = useToast();
  const { vendorId } = useParams();

  const { setData: setVendor } = useVendorStore();

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      pan: "",
    },
  });

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    if (!vendorId) {
      return;
    }
    const response = await verifyPanHttp({
      body: {
        fullName: data.fullName,
        pan: data.pan,
      },
      pathParams: {
        vendorId,
      },
    });

    if (!response.success) {
      return handleHttpError(response, toast);
    }
    toast({
      content: "PAN verified successfully",
      toastType: "success",
    });
    setVendor(response.data.vendor);
    document.getElementById("closeSheet")?.click();
  }

  // Show form validation errors as toast
  useEffect(() => {
    if (form.formState.errors) {
      const key = Object.keys(form.formState.errors)[0];
      if (key) {
        toast({
          content:
            form.formState.errors[key as keyof typeof form.formState.errors]
              ?.message || "",
          toastType: "error",
        });
      }
    }
  }, [form.formState.errors, toast]);

  return (
    <div className="flex flex-col w-full h-full">
      <SheetTitle className="p-5 border-b border-border font-semibold text-2xl text-white">
        Add PAN
      </SheetTitle>

      <div className="flex flex-col gap-10 p-5 w-full h-full">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col justify-between gap-8 w-full h-full"
          >
            <div className="flex flex-col gap-6 w-full">
              {/* Full Name */}
              <FormInput
                name="fullName"
                label="Enter full name"
                placeholder="Full name"
                form={form}
                required
                type="text"
              />

              {/* PAN */}
              <FormInput
                name="pan"
                label="Enter PAN number"
                placeholder="PAN"
                form={form}
                required
                type="text"
              />
            </div>

            {/* Submit Button */}
            <Button
              disabled={verfiyingPan || !form.formState.isValid}
              type="submit"
              className="flex gap-2 bg-primarybuttonbg py-8 w-full font-semibold text-primarybuttontext"
            >
              {verfiyingPan ? <Spinner width={20} /> : "Add PAN →"}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
}
